import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Button,
  Tabs,
  Tab,
  IconButton,
  TextField,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  "Certifications": "/Certifications",
  "Languages": "/Language",
  "Accomplishments": "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  "Software": "/SoftWare",
  "References": "/Refferences",
  "Volunteering": "/Volunteer",
  "Interests": "/Interest",
};

const theme = createTheme({
  palette: { primary: { main: '#002B5B' } },
  typography: { h4: { fontWeight: 600 }, h6: { fontWeight: 500 } },
});

const RichTextEditor = ({ value, onChange }) => {
  return (
    <Box>
      <TextField
        id="details-textarea"
        fullWidth
        multiline
        rows={6}
        variant="outlined"
        placeholder="Add your details here"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          mb: 1,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#B3D9F7" },
            "&:hover fieldset": { borderColor: "#0288D1" },
            "&.Mui-focused fieldset": { borderColor: "#002B5B" },
          },
        }}
      />
    </Box>
  );
};

const AdditionalInfoForm = () => {
  const [details, setDetails] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [savedDetails, setSavedDetails] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const navigate = useNavigate();
  const { sections } = useSections();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedDetails(stored);
  }, []);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleSaveAndNext = () => {
    // Save if details exist
    if (details.trim()) {
      let updated;
      if (editIndex !== null) {
        updated = [...savedDetails];
        updated[editIndex] = details;
        setEditIndex(null);
      } else {
        updated = [...savedDetails, details];
      }

      setSavedDetails(updated);
      localStorage.setItem("additionalInfo", JSON.stringify(updated));
      setDetails('');
    }

    // Always navigate to next section
    const currentSection = "Additional Information";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex !== -1 && currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", {
        state: { selectedTemplate, selectedTheme },
      });
    }
  };

  const handleDelete = (index) => {
    const updated = savedDetails.filter((_, i) => i !== index);
    setSavedDetails(updated);
    localStorage.setItem("additionalInfo", JSON.stringify(updated));
  };

  const handleEdit = (index) => {
    setDetails(savedDetails[index]);
    setEditIndex(index);
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Additional Information
          </Typography>
        </Toolbar>
      </AppBar>

      <ThemeProvider theme={theme}>
        <Container
          maxWidth="md"
          sx={{
            py: { xs: 2, md: 4 },
            px: { xs: 2, sm: 3, md: 4 },
            mx: "auto",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 3,
              border: "1px solid #B3D9F7",
              boxShadow: "0 4px 20px rgba(0, 43, 91, 0.12)",
              backgroundColor: "#ffffff",
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: "#002B5B", borderBottom: "2px solid #0288D1", pb: 1 }}
            >
              Additional Information
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: "#0288D1", mb: 3 }}>
              Add anything else you want employers to know.
            </Typography>

            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#B3D9F7",
                mb: 2,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  "& .MuiTab-root": { color: "#0D4C8B", fontWeight: "bold" },
                  "& .MuiTab-root.Mui-selected": { color: "#002B5B" },
                  "& .MuiTabs-indicator": { backgroundColor: "#0288D1" },
                }}
              >
                <Tab label="Details" />
              </Tabs>
            </Box>

            {tabValue === 0 && <RichTextEditor value={details} onChange={setDetails} />}

            <Divider sx={{ my: 3, borderColor: "#B3D9F7" }} />

            {/* Saved Cards */}
            <Box sx={{ mb: 3 }}>
              {savedDetails.map((item, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                    border: "1px solid #B3D9F7",
                    borderLeft: "4px solid #002B5B",
                    borderRadius: 2,
                    "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
                  }}
                >
                  <CardContent>
                    <Typography variant="body1" sx={{ color: "#0D4C8B" }}>
                      {item}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      onClick={() => handleEdit(index)}
                      sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: "#0288D1",
                  color: "#002B5B",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#E1F5FE", borderColor: "#002B5B" },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveAndNext}
                sx={{
                  px: 4,
                  fontWeight: "bold",
                  backgroundColor: "#002B5B",
                  "&:hover": { backgroundColor: "#0288D1" },
                  borderRadius: 2,
                  boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
                }}
              >
                Save & Next
              </Button>
            </Box>
          </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default AdditionalInfoForm;